import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  CalendarPlus,
  Plus,
  Calendar,
  User,
  FileText,
  LogOut,
  X,
  Gavel,
  Eye,
  CreditCard
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import auctionViewerService from '../../../services/auctionViewerService';
// Same logo as easyauction.png with its empty padding trimmed off, so it can
// fill the header at a readable size without making the header taller.
import easyAuctionLogo from '../../../assets/easyauction-trimmed.png';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  // A company viewer (2nd auctioneer added by a channel partner) is view-only:
  // their whole nav is the auctions shared with them plus their profile.
  const isViewer = user?.role === "auction_viewer";

  // Everyone else only gets a "Shared With Me" entry if an auction has
  // actually been shared with them (e.g. an existing user added as a viewer),
  // so it doesn't clutter the menu for the vast majority who have none.
  const [hasShared, setHasShared] = useState(false);
  useEffect(() => {
    if (!user || isViewer) return;
    let cancelled = false;
    auctionViewerService
      .getShared()
      .then((list) => {
        if (!cancelled) setHasShared(list.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasShared(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, isViewer]);

  const viewerNavigation = [
    { name: "Shared Auctions", href: "/dashboard/shared", icon: Eye },
    { name: "My Profile", href: "/dashboard/profile", icon: User },
  ];

  const fullNavigation = [
    { name: "Dashboard", href: "/dashboard", icon: Home },
    // { name: "My Auctions", href: "/dashboard/auctions", icon: Gavel },
    {
      name: "My Created Auction As Auctioneer",
      href: "/dashboard/MyCreatedA",
      icon: Gavel,
    },
    {
      name: "My Participated Auction As Participant",
      href: "/dashboard/MyParticipatedA",
      icon: CalendarPlus,
    },
    { name: "New Auction", href: "/dashboard/new-auction", icon: Plus },
    { name: "Reports", href: "/dashboard/reports", icon: FileText },
    ...(hasShared
      ? [{ name: "My Auction As Second Auctioneer", href: "/dashboard/shared", icon: Eye }]
      : []),
    { name: "Plans & Billing", href: "/dashboard/plans", icon: CreditCard },
    { name: "My Profile", href: "/dashboard/profile", icon: User },
  ];

  const navigation = isViewer ? viewerNavigation : fullNavigation;

  const handleLogout = () => {
    logout();
  };

  return (
    <aside
      className={`ap-sidebar ${isOpen ? 'ap-sidebar-open' : 'ap-sidebar-closed'}`}
      aria-hidden={!isOpen && window.innerWidth < 1024}
      id="dashboard-sidebar"
    >
      {/* Sidebar Header */}
      <div className="ap-sidebar-header">
        <Link
          to="/dashboard"
          className="ap-sidebar-logo-section"
          aria-label="EasyEAuction dashboard home"
          onClick={() => {
            if (window.innerWidth < 1024) onClose();
          }}
        >
          <img
            src={easyAuctionLogo}
            alt="EasyEAuction logo"
            className="ap-sidebar-logo-image"
          />
        </Link>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="ap-sidebar-close-btn"
          aria-label="Close sidebar"
        >
          <X className="ap-sidebar-close-icon" />
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="ap-sidebar-nav" aria-label="Dashboard navigation">
        <span className="ap-sidebar-nav-heading">Menu</span>
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive =
            location.pathname === item.href ||
            (item.href !== '/dashboard' &&
              location.pathname.startsWith(item.href));

          return (
            <Link
              key={item.name}
              to={item.href}
              className={`ap-sidebar-nav-link ${
                isActive ? 'ap-sidebar-nav-link-active' : ''
              }`}
              onClick={() => {
                if (window.innerWidth < 1024) {
                  onClose();
                }
              }}
            >
              <Icon className="ap-sidebar-nav-icon" />
              <span className="ap-sidebar-nav-text">{item.name}</span>
            </Link>
          );
        })}
      </nav>

      {/* Sidebar Footer */}
      <div className="ap-sidebar-footer">
        {/* User Info */}
        <div className="ap-sidebar-user-info">
          <div className="ap-sidebar-user-top">
            <div className="ap-sidebar-user-avatar" aria-hidden="true">
              {(user?.personName || user?.name || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="ap-sidebar-user-text">
              <div className="ap-sidebar-user-name">
                {user?.personName || user?.name || 'User'}
              </div>
              <div className="ap-sidebar-user-company">
                {user?.companyName || 'No Company'}
              </div>
            </div>
          </div>
          <div className="ap-sidebar-user-phone">
            {user?.phoneNumber?.startsWith('+91')
              ? user.phoneNumber.replace('+91', '+91\u00A0')
              : user?.phoneNumber}
          </div>

          {/* coloured role badge */}
          <div className="ap-sidebar-user-role">
            {user?.role === 'admin' ? (
              <span className="ap-role-badge ap-role-admin">
                System Administrator
              </span>
            ) : user?.role === 'channel_partner' ? (
              <span className="ap-role-badge ap-role-channel-partner">
                Channel Partner
              </span>
            ) : user?.role === 'auction_viewer' ? (
              <span className="ap-role-badge ap-role-viewer">
                Company Viewer
              </span>
            ) : user?.role === 'auctioneer' ? (
              <span className="ap-role-badge ap-role-auctioneer">
                User
              </span>
            ) : (
              <span className="ap-role-badge ap-role-participant">User</span>
            )}
          </div>
        </div>

        {/* Logout Button */}
        <button onClick={handleLogout} className="ap-sidebar-logout-btn">
          <LogOut className="ap-sidebar-logout-icon" />
          <span className="ap-sidebar-logout-text">Sign out</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;

